<script setup lang="ts">
    /**
     * A checkbox (or switch) with a bold label and an optional hint below it, the
     * shape settings panels use for every boolean option. It exists because that
     * markup was copied in ~30 places: same three elements, same utility classes,
     * drifting one edit at a time.
     *
     * Layout only. Wording stays with the caller, so labels keep living next to
     * the option they describe (and in the caller's i18n keys).
     *
     * Extra content under the hint (badges, a channel-support strip) goes in the
     * default slot; options that need a whole custom body are better off with
     * `VvCheckbox` directly.
     *
     * Anything else is forwarded to `VvCheckbox`, `switch` included: options that
     * read as on/off rather than opt-in pass it as a plain attribute.
     */
    const model = defineModel<boolean>({ default: false })

    defineProps<{
        label: string
        hintLabel?: string
        /** Field name, unique within the form. */
        name?: string
        disabled?: boolean
    }>()
</script>

<template>
    <VvCheckbox
        v-model="model"
        class="flex-nowrap"
        :name
        :value="true"
        :unchecked-value="false"
        :disabled>
        <div class="flex flex-col ml-12 gap-2">
            <strong class="font-semibold">
                {{ label }}
            </strong>
            <small v-if="hintLabel" class="text-12 text-word-3">
                {{ hintLabel }}
            </small>
            <slot />
        </div>
    </VvCheckbox>
</template>
